import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, FileText, Download, Loader2, ListMusic } from "lucide-react";
import { getErrorMessage } from "../../utils/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

/**
 * SongImportDialog — two ways to import a song:
 *  1. CCLI/Title lookup against free public lyrics sources
 *  2. Paste lyrics → AI auto-detects Verse/Chorus/Bridge structure
 *
 * On success, calls onImported({ chart_id, title }) so the parent can refresh.
 */
export default function SongImportDialog({ communityId, t, onImported }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("lookup");
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState(null);

  // Lookup form
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [ccli, setCcli] = useState("");

  // Paste form
  const [pastedLyrics, setPastedLyrics] = useState("");

  const reset = () => {
    setParsed(null);
    setTitle(""); setArtist(""); setCcli(""); setPastedLyrics("");
    setMode("lookup");
  };

  const doLookup = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/music/import/lookup`,
        { title: title.trim(), artist: artist.trim() || null, ccli_number: ccli.trim() || null },
        { headers: auth() });
      setParsed(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, t("lookup_failed") || "Lookup failed"));
    } finally { setLoading(false); }
  };

  const doParse = async () => {
    if (pastedLyrics.trim().length < 20) {
      toast.error("Paste at least a verse of lyrics"); return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/music/import/parse-pasted`,
        { lyrics: pastedLyrics, title: title.trim() || null, artist: artist.trim() || null,
          ccli_number: ccli.trim() || null },
        { headers: auth() });
      setParsed(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, t("import_failed") || "Import failed"));
    } finally { setLoading(false); }
  };

  const saveChart = async () => {
    if (!parsed) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/music/import/save-chart`,
        { community_id: communityId, title: parsed.title, artist: parsed.artist,
          ccli_number: parsed.ccli_number, copyright: parsed.copyright,
          sections: parsed.sections, tags: [] },
        { headers: auth() });
      toast.success(t("song_imported") || "Song imported");
      onImported && onImported(res.data);
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(getErrorMessage(err, t("import_failed") || "Import failed"));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-2" data-testid="import-song-btn">
          <Download className="w-4 h-4" />
          {t("import_song") || "Import Song"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListMusic className="w-5 h-5 text-pink-600" />
            {t("import_song") || "Import Song"}
          </DialogTitle>
          <DialogDescription>
            {t("import_song_desc") || "Look up lyrics from free sources, or paste lyrics and let AI detect verse/chorus structure."}
          </DialogDescription>
        </DialogHeader>

        {!parsed ? (
          <Tabs value={mode} onValueChange={setMode} className="mt-3">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="lookup" data-testid="import-tab-lookup">
                <Search className="w-3.5 h-3.5 mr-1" />
                {t("ccli_lookup") || "CCLI / Title Lookup"}
              </TabsTrigger>
              <TabsTrigger value="paste" data-testid="import-tab-paste">
                <FileText className="w-3.5 h-3.5 mr-1" />
                {t("paste_lyrics") || "Paste Lyrics"}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="lookup" className="space-y-3 pt-4">
              <div>
                <Label>{t("song_title") || "Song title"} *</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Amazing Grace" data-testid="import-title" />
              </div>
              <div>
                <Label>{t("song_artist") || "Artist (optional)"}</Label>
                <Input value={artist} onChange={(e) => setArtist(e.target.value)}
                  placeholder="e.g., Chris Tomlin" data-testid="import-artist" />
              </div>
              <div>
                <Label>{t("ccli_number") || "CCLI Number (optional)"}</Label>
                <Input value={ccli} onChange={(e) => setCcli(e.target.value)}
                  placeholder="e.g., 22025" data-testid="import-ccli" />
              </div>
              <Button onClick={doLookup} disabled={loading} className="w-full bg-pink-600 hover:bg-pink-700" data-testid="import-lookup-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                {loading ? (t("searching") || "Searching...") : (t("search") || "Search")}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                {t("import_lookup_note") || "Uses free public lyrics sources. If not found, switch to Paste Lyrics."}
              </p>
            </TabsContent>

            <TabsContent value="paste" className="space-y-3 pt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("song_title") || "Title"}</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("optional") || "Optional — AI will guess"} data-testid="paste-title" />
                </div>
                <div>
                  <Label>{t("song_artist") || "Artist"}</Label>
                  <Input value={artist} onChange={(e) => setArtist(e.target.value)}
                    placeholder={t("optional") || "Optional"} data-testid="paste-artist" />
                </div>
              </div>
              <div>
                <Label>{t("paste_lyrics") || "Paste Lyrics"} *</Label>
                <Textarea value={pastedLyrics} onChange={(e) => setPastedLyrics(e.target.value)}
                  placeholder="Paste lyrics from SongSelect, Genius, or anywhere else..."
                  className="min-h-[260px] font-mono text-xs" data-testid="paste-lyrics-input" />
              </div>
              <Button onClick={doParse} disabled={loading} className="w-full bg-pink-600 hover:bg-pink-700" data-testid="parse-pasted-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                {loading ? (t("parsing") || "Parsing...") : (t("structure_my_notes") || "Auto-Detect Sections")}
              </Button>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3 p-3 rounded-lg border border-pink-500/20 bg-pink-500/5">
              <div>
                <Label className="text-xs">{t("song_title") || "Title"}</Label>
                <Input value={parsed.title || ""} onChange={(e) => setParsed({ ...parsed, title: e.target.value })}
                  data-testid="review-title" />
              </div>
              <div>
                <Label className="text-xs">{t("song_artist") || "Artist"}</Label>
                <Input value={parsed.artist || ""} onChange={(e) => setParsed({ ...parsed, artist: e.target.value })}
                  data-testid="review-artist" />
              </div>
              <div>
                <Label className="text-xs">{t("ccli_number") || "CCLI #"}</Label>
                <Input value={parsed.ccli_number || ""} onChange={(e) => setParsed({ ...parsed, ccli_number: e.target.value })}
                  data-testid="review-ccli" />
              </div>
              <div>
                <Label className="text-xs">© Copyright</Label>
                <Input value={parsed.copyright || ""} onChange={(e) => setParsed({ ...parsed, copyright: e.target.value })}
                  data-testid="review-copyright" />
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                {t("review_sections") || "Review detected sections"}
                <Badge variant="outline" className="text-[10px]">{parsed.source}</Badge>
              </h4>
              <ScrollArea className="h-[300px] pr-2">
                <div className="space-y-2" data-testid="review-sections-list">
                  {(parsed.sections || []).map((s, i) => (
                    <div key={i} className="p-2 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                      <div className="flex items-center justify-between mb-1">
                        <Input
                          value={s.label}
                          onChange={(e) => {
                            const next = [...parsed.sections];
                            next[i] = { ...next[i], label: e.target.value };
                            setParsed({ ...parsed, sections: next });
                          }}
                          className="text-xs font-semibold h-7 w-40 bg-transparent"
                          data-testid={`section-label-${i}`}
                        />
                        <Button
                          variant="ghost" size="sm"
                          className="h-6 px-2 text-[10px] text-red-500"
                          onClick={() => {
                            const next = parsed.sections.filter((_, idx) => idx !== i);
                            setParsed({ ...parsed, sections: next });
                          }}
                          data-testid={`remove-section-${i}`}
                        >Remove</Button>
                      </div>
                      <Textarea
                        value={s.text}
                        onChange={(e) => {
                          const next = [...parsed.sections];
                          next[i] = { ...next[i], text: e.target.value };
                          setParsed({ ...parsed, sections: next });
                        }}
                        className="text-xs font-mono min-h-[80px]"
                        data-testid={`section-text-${i}`}
                      />
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}

        <DialogFooter>
          {parsed && (
            <Button variant="outline" onClick={() => setParsed(null)} data-testid="back-to-input-btn">
              {t("back") || "Back"}
            </Button>
          )}
          {parsed && (
            <Button onClick={saveChart} disabled={loading} className="bg-pink-600 hover:bg-pink-700" data-testid="save-imported-chart-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t("save_chart") || "Save Chart"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
